import { APP_HOST } from '@utils/apiHooks';
import { downloadICS, useMobileWidth } from '@utils/hooks';

import Button from '@components/Button';
import dayjs from 'dayjs';
import { formatDateToICS } from '@utils/time';
import { getEventDetails } from '@store/slices/eventSlice';
import { useInbound } from '@utils/Inbound/useInbound';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

interface SaveTheDateProps {
  label: string;
  buttonType: string;
  eventId?: string;
  padding?: string;
  width?: string;
  variant?: string;
}

const SaveTheDate = ({
  label,
  buttonType,
  eventId,
  padding,
  width,
  variant = 'primary',
}: SaveTheDateProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const isMobile = useMobileWidth();

  const { actionInbound, inboundCookie } = useInbound();

  const eventDescription = useMemo(() => {
    const eventDescriptionItems = [
      {
        title: 'agenda',
        value: `${APP_HOST}/events/${sluggedEvent?.slug}${t('agenda-url')}`,
      },
      {
        title: 'attendee-list',
        value: `${APP_HOST}/events/${sluggedEvent?.slug}${t('community-url')}`,
      },
    ];

    const keyItems = eventDescriptionItems
      .map(item => `${t(item.title)}: ${item.value}`)
      .join('\\n');

    return `${sluggedEvent?.description}\\n\\n${keyItems}`;
  }, [
    sluggedEvent?.location?.marker?.city,
    sluggedEvent?.location?.name,
    sluggedEvent?.name,
    sluggedEvent?.slug,
    t,
  ]);

  const icsContent = useMemo(
    () =>
      `BEGIN:VCALENDAR
VERSION:2.0
CHARSET=ISO-8859-1
PRODID:-//we.CONECT Global Leaders//Website//EN
CALSCALE:GREGORIAN
BEGIN:VEVENT
UID:${btoa(sluggedEvent?.name)}
DTSTAMP:${dayjs().unix()}
DTSTART:${formatDateToICS(sluggedEvent?.start_date)}
DTEND:${formatDateToICS(sluggedEvent?.end_date)}
SUMMARY:${sluggedEvent?.name}
DESCRIPTION:${t('save-the-date-description', { eventName: sluggedEvent?.name, eventShortDescription: eventDescription })}
LOCATION:${sluggedEvent?.location?.name ?? ''}
URL:${APP_HOST}/events/${sluggedEvent?.slug}
BEGIN:VALARM
ACTION:DISPLAY
TRIGGER:-PT20160M
DESCRIPTION:Reminder
END:VALARM
END:VEVENT
END:VCALENDAR`,
    [
      eventDescription,
      sluggedEvent?.end_date,
      sluggedEvent?.location?.name,
      sluggedEvent?.name,
      sluggedEvent?.start_date,
    ]
  );

  return (
    <Button
      title={label}
      backgroundColor={
        variant === 'primary'
          ? sluggedEvent?.design?.themeColor
          : theme.color.white
      }
      hoverBackgroundColor={theme.color.mobilityGrey}
      textColor={
        variant === 'primary'
          ? theme.color.white
          : sluggedEvent?.design?.themeColor
      }
      lineHeight={theme.lineHeight.height150}
      fontSize={isMobile ? theme.fontSize.size18 : theme.fontSize.size20}
      borderRadius={theme.borderRadius.radius10}
      padding={padding ?? (isMobile ? '1.5rem 2.5rem' : '1.1rem 4.9rem')}
      width={width ?? (isMobile ? '100%' : 'max-content')}
      onClick={() => {
        void (async () => {
          downloadICS(icsContent, 'calendar', t);

          if (inboundCookie) {
            await actionInbound({
              ...JSON.parse(inboundCookie ?? '{}'),
              event: sluggedEvent?.id,
              path: window.location.href,
              action: 'calendar',
            });
          }
        })();
      }}
    />
  );
};

export default SaveTheDate;
